import { useEffect, useRef, useState } from 'react';
import useFetch from 'use-http';

interface ICheckNumberValidationOptions {
    checkNo: string | number | null | undefined;
    compId: string | number | null | undefined;
    missingCompanyMessage?: string;
    requestFailureMessage?: string;
}

interface ICheckNumberValidationResult {
    isValidating: boolean;
    validationError: string | null;
    isCheckAvailable: boolean;
}

/** Shared, debounced availability check used by each Lease Records check-creation dialog. */
const useCheckNumberValidation = ({
    checkNo,
    compId,
    missingCompanyMessage,
    requestFailureMessage,
}: ICheckNumberValidationOptions): ICheckNumberValidationResult => {
    const { post, response } = useFetch();
    const isMounted = useRef(true);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isCheckAvailable, setIsCheckAvailable] = useState(false);

    useEffect(
        () => () => {
            isMounted.current = false;
        },
        []
    );

    useEffect(() => {
        let isActive = true;

        if (!checkNo || !compId) {
            setValidationError(checkNo && !compId && missingCompanyMessage ? missingCompanyMessage : null);
            setIsCheckAvailable(false);
            setIsValidating(false);
            return () => {
                isActive = false;
            };
        }

        setValidationError(null);
        setIsCheckAvailable(false);
        setIsValidating(true);

        const timerId = window.setTimeout(async () => {
            if (!isActive) {
                return;
            }

            try {
                const parsedCheckNo = parseInt(String(checkNo), 10);
                const parsedCompId = parseInt(String(compId), 10);
                if (isNaN(parsedCheckNo) || isNaN(parsedCompId)) {
                    return;
                }

                const result = await post('/v1.0/leases/master/validate-check-number', {
                    checkNo: parsedCheckNo,
                    compId: parsedCompId,
                });
                if (!isActive || !isMounted.current) {
                    return;
                }

                if (response.ok) {
                    const available = result.status === 0;
                    setValidationError(available ? null : result.message || 'Unavailable');
                    setIsCheckAvailable(available);
                } else if (requestFailureMessage) {
                    setValidationError(requestFailureMessage);
                }
            } catch {
                // Preserve the existing dialogs' behavior: a failed validation request
                // leaves the check unavailable without replacing the surrounding error UI.
            } finally {
                if (isActive && isMounted.current) {
                    setIsValidating(false);
                }
            }
        }, 1000);

        return () => {
            isActive = false;
            window.clearTimeout(timerId);
        };
    }, [checkNo, compId, missingCompanyMessage, post, requestFailureMessage, response]);

    return { isValidating, validationError, isCheckAvailable };
};

export default useCheckNumberValidation;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/useCheckNumberValidation.ts